// EmailNotifications.js — drop into src/
// Utility functions to queue emails for key platform events
// Uses email_queue table — picked up by Supabase Edge Function or cron
//
// Usage:
//   import { sendWelcomeEmail, sendEndorsementApproved, ... } from "./EmailNotifications";
//   await sendWelcomeEmail(supabase, { name, email, role });

const PLATFORM = "OJT Hub";
const PLATFORM_URL = "https://philippineinternships.com";

// ── Core queue function ───────────────────────────────────────────────────────
async function queueEmail(supabase, { to_email, template, data }) {
  if (!to_email || !template) return;
  // Don't send to demo/deleted accounts
  if (to_email.includes("@removed.local") || to_email.includes("@ojthub.ph")) return;
  try {
    await supabase.from("email_queue").insert({
      to_email,
      template,
      data: { ...data, platform: PLATFORM, platform_url: PLATFORM_URL },
      status: "pending",
    });
  } catch(e) {
    console.warn("Email queue error:", e.message);
  }
}

// ── 1. Welcome email — sent on new registration ───────────────────────────────
export async function sendWelcomeEmail(supabase, { name, email, role = "student" }) {
  const roleMessages = {
    student:          "Start by completing your profile and browsing OJT opportunities.",
    employer:         "Post your first internship listing and start receiving applications.",
    coordinator:      "Access your university portal to manage students and endorsements.",
    university_admin: "Set up your university profile and invite coordinators.",
  };
  await queueEmail(supabase, {
    to_email: email,
    template: "welcome",
    data: {
      name,
      email,
      role,
      message: roleMessages[role] || roleMessages.student,
    },
  });
}

// ── 2. Endorsement status notifications ───────────────────────────────────────
export async function sendEndorsementApproved(supabase, {
  studentEmail, studentName, companyName, coordinatorName
}) {
  await queueEmail(supabase, {
    to_email: studentEmail,
    template: "endorsement_status",
    data: {
      student_name:      studentName,
      company_name:      companyName,
      coordinator_name:  coordinatorName,
      new_status:        "accepted",
      cta_url:           `${PLATFORM_URL}/app`,
      cta_label:         "View My Endorsement →",
    },
  });
}

export async function sendEndorsementSubmitted(supabase, {
  coordinatorEmail, coordinatorName, studentName, companyName
}) {
  await queueEmail(supabase, {
    to_email: coordinatorEmail,
    template: "endorsement_submitted",
    data: {
      coordinator_name: coordinatorName,
      student_name:     studentName,
      company_name:     companyName,
      cta_url:          `${PLATFORM_URL}/university`,
      cta_label:        "Review Endorsement →",
    },
  });
}

// ── 3. Logbook notifications ──────────────────────────────────────────────────
export async function sendLogbookApproved(supabase, {
  studentEmail, studentName, hours, totalApproved, required
}) {
  const pct = Math.round((totalApproved / required) * 100);
  await queueEmail(supabase, {
    to_email: studentEmail,
    template: "logbook_coordinator_approved",
    data: {
      student_name:  studentName,
      hours,
      entry_date:    new Date().toLocaleDateString("en-PH", { month:"long", day:"numeric", year:"numeric" }),
      total_hours:   totalApproved,
    },
  });
}

// ── 4. Application notifications ──────────────────────────────────────────────
export async function sendApplicationReceived(supabase, {
  employerEmail, employerName, studentName, jobTitle
}) {
  await queueEmail(supabase, {
    to_email: employerEmail,
    template: "application_submitted",
    data: {
      employer_name: employerName,
      student_name:  studentName,
      job_title:     jobTitle,
      cta_url:       `${PLATFORM_URL}/employer`,
      cta_label:     "Review Application →",
    },
  });
}

export async function sendApplicationStatusUpdate(supabase, {
  studentEmail, studentName, jobTitle, companyName, status
}) {
  const statusMessages = {
    reviewed:   { subject:"Your application was reviewed", msg:"The employer has reviewed your application." },
    shortlisted:{ subject:"🎉 You've been shortlisted!", msg:"Great news! You've been shortlisted for an interview." },
    accepted:   { subject:"🎉 Application Accepted!", msg:"Congratulations! Your application has been accepted." },
    rejected:   { subject:"Application Update", msg:"Thank you for applying. Unfortunately, this position has been filled." },
  };
  const meta = statusMessages[status] || statusMessages.reviewed;
  await queueEmail(supabase, {
    to_email: studentEmail,
    template: "application_status",
    data: {
      student_name: studentName,
      job_title:    jobTitle,
      company_name: companyName,
      status,
      status_message: meta.msg,
      subject:        meta.subject,
      cta_url:        `${PLATFORM_URL}/app`,
      cta_label:      "View Application →",
    },
  });
}

// ── 5. Certificate ready ──────────────────────────────────────────────────────
export async function sendCertificateReady(supabase, {
  studentEmail, studentName, certNumber
}) {
  await queueEmail(supabase, {
    to_email: studentEmail,
    template: "offer_letter",
    data: {
      student_name: studentName,
      cert_number:  certNumber,
      verify_url:   `${PLATFORM_URL}/verify/${certNumber}`,
      cta_url:      `${PLATFORM_URL}/certificate`,
      cta_label:    "Download My Certificate →",
    },
  });
}

// ── 6. University — student completion ────────────────────────────────────────
export async function sendStudentCompleted(supabase, {
  coordinatorEmail, coordinatorName, studentName, totalHours
}) {
  await queueEmail(supabase, {
    to_email: coordinatorEmail,
    template: "completion_risk",
    data: {
      coordinator_name: coordinatorName,
      student_name:     studentName,
      total_hours:      totalHours,
      cta_url:          `${PLATFORM_URL}/university`,
      cta_label:        "View Student Records →",
    },
  });
}

// ── 7. Password reset (manual trigger) ────────────────────────────────────────
export async function sendPasswordResetEmail(supabase, { email, resetUrl }) {
  await queueEmail(supabase, {
    to_email: email,
    template: "welcome",
    data: {
      reset_url:  resetUrl,
      expires_in: "1 hour",
      cta_url:    resetUrl,
      cta_label:  "Reset My Password →",
    },
  });
}

// ── 8. Broadcast message from university ──────────────────────────────────────
export async function sendBroadcastEmail(supabase, {
  recipients, subject, message, senderName, senderTitle
}) {
  const inserts = recipients.map(r => ({
    to_email: r.email,
    template: "broadcast",
    data: {
      recipient_name: r.full_name || "Student",
      subject,
      message,
      sender_name:  senderName,
      sender_title: senderTitle,
      cta_url:      `${PLATFORM_URL}/app`,
      cta_label:    "Open OJT Hub →",
    },
    status: "pending",
  }));

  // Batch insert in chunks of 50 to avoid payload limits
  const CHUNK = 50;
  for (let i = 0; i < inserts.length; i += CHUNK) {
    await supabase.from("email_queue").insert(inserts.slice(i, i + CHUNK));
  }
  return inserts.length;
}
